import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../../components/layout/Layout';
import { PageHeader } from '../../components/layout/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Spinner } from '../../components/ui/Spinner';
import { Button } from '../../components/ui/Button';
import { Switch } from '../../components/ui/Switch';
import { getSchool, patchSchoolFeatureFlags } from '../../modules/platform/api/schools';
import { patchSchoolLanguage, getMySchoolLanguage } from '../../modules/platform/api/schools';
import { School } from '../../modules/platform/types';
import type { SchoolFeatureFlagKey, SchoolFeatureFlagsSnapshot } from '../../modules/platform/types/school';
import { showErrorToast, showSuccessToast } from '../../utils/errorHandler';
import { getSchoolOnboardingListLabel } from '../../utils/onboardingDisplay';
import {
  buildFeatureFlagsSnapshot,
  CORE_SCHOOL_FEATURE_FLAGS,
  LEARNING_EXPERIENCE_FEATURE_FLAGS,
} from './schoolFeatureFlagsConfig';
import { getPromptLibraryDialects, DialectProfileAdmin } from '../../modules/tutor/api/promptLibraryAdminService';

export const SchoolDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [school, setSchool] = useState<School | null>(null);
  const [loading, setLoading] = useState(true);
  const [featureFlagsDraft, setFeatureFlagsDraft] = useState<SchoolFeatureFlagsSnapshot | null>(null);
  const [savingFlags, setSavingFlags] = useState(false);
  // Dialects state
  const [dialects, setDialects] = useState<DialectProfileAdmin[]>([]);
  const [dialectsLoading, setDialectsLoading] = useState(false);
  const [selectedDialectKeys, setSelectedDialectKeys] = useState<string[]>([]);
  const [savingLanguage, setSavingLanguage] = useState(false);

  useEffect(() => {
    if (!id) return;

    const loadSchool = async () => {
      try {
        const data = await getSchool(id);
        setSchool(data);
      } catch (error) {
        showErrorToast(error);
        navigate('/dev/schools');
      } finally {
        setLoading(false);
      }
    };

    loadSchool();
  }, [id, navigate]);

  useEffect(() => {
    if (school) {
      setFeatureFlagsDraft(buildFeatureFlagsSnapshot(school));
    }
  }, [school]);

  useEffect(() => {
    const loadDialects = async () => {
      setDialectsLoading(true);
      try {
        const data = await getPromptLibraryDialects();

        // Filter only active dialects, dedupe by key keeping highest version, sort by name.
        const active = (data ?? []).filter((d) => d.status === 'active' || d.status === undefined);
        const byKey = new Map<string, DialectProfileAdmin>();
        for (const d of active) {
          const existing = byKey.get(d.key);
          if (!existing || (d.version ?? 0) > (existing.version ?? 0)) {
            byKey.set(d.key, d);
          }
        }
        const uniq = Array.from(byKey.values()).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setDialects(uniq);
      } catch (error) {
        showErrorToast(error);
      } finally {
        setDialectsLoading(false);
      }
    };

    loadDialects();
  }, []);

  // Initialize selectedDialectKeys from school when it loads — preserve saved keys even if not active.
  useEffect(() => {
    if (!school) return;
    const keys = school.school_settings?.language?.preferred_dialect_keys ?? [];
    setSelectedDialectKeys(keys && keys.length ? keys : ['standard_tamil']);
  }, [school]);

  const uniqueKeys = (keys: string[]) => {
    const seen = new Set<string>();
    return keys
      .map((k) => (k ? k.trim() : ''))
      .filter((k) => {
        if (!k) return false;
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      });
  };

  const addDialect = (key: string) => {
    setSelectedDialectKeys((prev) => uniqueKeys([...prev, key]));
  };

  const removeDialect = (key: string) => {
    setSelectedDialectKeys((prev) => prev.filter((item) => item !== key));
  };

  const moveDialect = (index: number, direction: -1 | 1) => {
    setSelectedDialectKeys((prev) => {
      const next = [...prev];
      const target = index + direction;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const handleSaveSchoolLanguage = async () => {
    if (!id) return;

    const cleaned = uniqueKeys(selectedDialectKeys);
    if (!cleaned.length) {
      showErrorToast(new Error('Select at least one dialect'));
      return;
    }

    setSavingLanguage(true);
    try {
      const updated = await patchSchoolLanguage(id, { language: { preferred_dialect_keys: cleaned } });
      setSchool(updated);
      setSelectedDialectKeys(
        updated.school_settings?.language?.preferred_dialect_keys && updated.school_settings.language.preferred_dialect_keys.length
          ? updated.school_settings.language.preferred_dialect_keys
          : cleaned
      );
      showSuccessToast('School dialect preferences saved');
    } catch (error) {
      showErrorToast(error);
    } finally {
      setSavingLanguage(false);
    }
  };

  const setFeatureFlag = (key: SchoolFeatureFlagKey, value: boolean) => {
    setFeatureFlagsDraft((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  const handleSaveFeatureFlags = async () => {
    if (!id || !featureFlagsDraft) return;
    setSavingFlags(true);
    try {
      await patchSchoolFeatureFlags(id, { feature_flags: featureFlagsDraft });
      const fresh = await getSchool(id);
      setSchool(fresh);
      setFeatureFlagsDraft(buildFeatureFlagsSnapshot(fresh));
      showSuccessToast('Core features saved');
    } catch (error) {
      showErrorToast(error);
    } finally {
      setSavingFlags(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center py-8">
          <Spinner size="lg" />
        </div>
      </Layout>
    );
  }

  if (!school) return null;

  return (
    <Layout>
      <div className="mb-4 flex justify-end">
        <Button variant="ghost" onClick={() => navigate('/dev/schools')}>
          ← Back
        </Button>
      </div>

      <PageHeader title={school.name} subtitle={`Code: ${school.code || '-'} · Status: ${school.status || '-'}`} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        {/* Card A — Snapshot */}
        <Card>
          <CardHeader>
            <CardTitle>Snapshot</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <InfoRow label="Name" value={school.name} />
            <InfoRow
              label="Status"
              value={<Badge variant={school.status === 'active' ? 'success' : 'gray'}>{school.status || '-'}</Badge>}
            />
            <InfoRow
              label="Onboarding"
              value={school.onboarding_state ? getSchoolOnboardingListLabel(school) : '-'}
            />
            <InfoRow label="Code" value={school.code} />
            <InfoRow label="Timezone" value={school.timezone} />
            <InfoRow label="Country" value={school.country} />
            <InfoRow label="Subdomain" value={school.subdomain} />
          </CardContent>
        </Card>

        {/* Card B — Details */}
        <Card>
          <CardHeader>
            <CardTitle>Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <InfoRow label="Phone" value={school.contact?.phone || school.contact_phone || school.phone} />
            <InfoRow
              label="Website"
              value={
                school.website ? (
                  <a href={school.website} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">
                    {school.website}
                  </a>
                ) : (
                  '-'
                )
              }
            />
            <div>
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Description</dt>
              <dd className="mt-1 text-sm text-gray-900 dark:text-white max-h-40 overflow-auto break-words">
                {school.description || '-'}
              </dd>
            </div>
            <InfoRow label="Board" value={school.board} />
          </CardContent>
        </Card>

        {/* Card C — Academic Year & Contact */}
        <Card>
          <CardHeader>
            <CardTitle>Academic Year & Contact</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <InfoRow
              label="Academic Year"
              value={
                school.school_settings?.academic_year ? (
                  <div className="space-y-1">
                    <div>Key: {school.school_settings.academic_year.key || '-'}</div>
                    <div>Start: {school.school_settings.academic_year.start_date || '-'}</div>
                    <div>End: {school.school_settings.academic_year.end_date || '-'}</div>
                  </div>
                ) : (
                  '-'
                )
              }
            />

            <InfoRow
              label="Contact"
              value={
                <div className="space-y-1">
                  <div>Email: {school.contact?.email || '-'}</div>
                  <div>Phone: {school.contact?.phone || '-'}</div>
                  <div>Address: <span className="break-words">{school.contact?.address || '-'}</span></div>
                </div>
              }
            />
          </CardContent>
        </Card>

        {/* Card D — Branding & Language */}
        <Card>
          <CardHeader>
            <CardTitle>Branding & Language</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <InfoRow
              label="Logo"
              value={
                school.branding?.logo_url ? (
                  // eslint-disable-next-line jsx-a11y/img-redundant-alt
                  <img src={school.branding.logo_url} alt="logo preview" className="h-12 object-contain rounded" />
                ) : (
                  '-'
                )
              }
            />

            <InfoRow
              label="Theme Color"
              value={
                <span className="inline-flex items-center gap-2">
                  <span
                    className="w-5 h-5 rounded-full border"
                    style={{ backgroundColor: school.branding?.theme_color || '#f3f4f6' }}
                  />
                  <span>{school.branding?.theme_color || '-'}</span>
                </span>
              }
            />

            <InfoRow
              label="Language"
              value={
                <div>
                  <div>UI Default: {school.school_settings?.language?.ui_default || '-'}</div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {(school.school_settings?.language?.allowed && school.school_settings.language.allowed.length)
                      ? school.school_settings.language.allowed.map((lang) => (
                          <span key={lang} className="px-2 py-1 rounded bg-gray-100 dark:bg-slate-800 text-xs">
                            {lang}
                          </span>
                        ))
                      : <span>-</span>}
                  </div>
                </div>
              }
            />
          </CardContent>
        </Card>

        {/* Tamil Dialect Preferences (Dev Team) */}
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-4">
            <div>
              <CardTitle>Tamil Dialect Preferences</CardTitle>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Select and order the dialects available for this school. The first dialect is used as default.
              </p>
            </div>
            <Button
              type="button"
              onClick={handleSaveSchoolLanguage}
              disabled={savingLanguage || dialectsLoading}
            >
              {savingLanguage ? 'Saving…' : 'Save dialect preferences'}
            </Button>
          </CardHeader>

          <CardContent className="space-y-6">
            {dialectsLoading ? (
              <div className="flex justify-center py-4">
                <Spinner />
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Selected dialects</h3>
                  <ul className="space-y-2">
                    {selectedDialectKeys.map((key, index) => {
                      const meta = dialects.find((d) => d.key === key);
                      const isSavedNotActive = !meta;
                      return (
                        <li
                          key={key}
                          className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 dark:border-slate-700 bg-gray-50/80 dark:bg-slate-900/40 px-3 py-2.5"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                {meta ? meta.name : key}
                              </div>
                              {index === 0 && <Badge variant="success">Default</Badge>}
                              {isSavedNotActive && <span className="ml-2 text-xs text-yellow-700 dark:text-yellow-300">Saved, not active</span>}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {meta ? `${meta.key}${meta.language ? ` · ${meta.language}` : ''}` : key}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              onClick={() => moveDialect(index, -1)}
                              disabled={index === 0}
                              title="Move up"
                            >
                              ↑
                            </Button>
                            <Button
                              variant="ghost"
                              onClick={() => moveDialect(index, 1)}
                              disabled={index === selectedDialectKeys.length - 1}
                              title="Move down"
                            >
                              ↓
                            </Button>
                            <Button
                              variant="danger"
                              onClick={() => removeDialect(key)}
                              disabled={selectedDialectKeys.length <= 1}
                              title="Remove dialect"
                            >
                              Remove
                            </Button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Available dialects</h3>
                  <div className="flex flex-wrap gap-2">
                    {dialects
                      .filter((d) => !selectedDialectKeys.includes(d.key))
                      .map((d) => (
                        <Button key={d.key} variant="outline" onClick={() => addDialect(d.key)}>
                          {d.name} <span className="ml-2 text-xs text-gray-400">({d.key})</span>
                        </Button>
                      ))}
                    {dialects.length === 0 && <div className="text-sm text-gray-500">No active dialects available</div>}
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Core features — Dev Team; full snapshot on save */}
        {featureFlagsDraft && (
          <Card className="lg:col-span-2">
            <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-4">
              <CardTitle>Core Features </CardTitle>
              <Button type="button" onClick={handleSaveFeatureFlags} disabled={savingFlags}>
                {savingFlags ? 'Saving…' : 'Save Core features'}
              </Button>
            </CardHeader>
            <CardContent className="space-y-6 sm:space-y-8 pt-2">
              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Core school features</h3>
                <ul className="space-y-2.5 list-none p-0 m-0">
                  {CORE_SCHOOL_FEATURE_FLAGS.map(({ key, label }) => (
                    <li
                      key={key}
                      className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 dark:border-slate-700 bg-gray-50/80 dark:bg-slate-900/40 px-3 py-2.5"
                    >
                      <span className="text-sm text-gray-800 dark:text-gray-200 min-w-0 flex-1 pr-2">{label}</span>
                      <Switch
                        checked={featureFlagsDraft[key]}
                        onCheckedChange={(v) => setFeatureFlag(key, v)}
                        disabled={savingFlags}
                        id={`ff-${key}`}
                      />
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                  Learning experience features
                </h3>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2.5 list-none p-0 m-0">
                  {LEARNING_EXPERIENCE_FEATURE_FLAGS.map(({ key, label }) => (
                    <li
                      key={key}
                      className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 dark:border-slate-700 bg-gray-50/80 dark:bg-slate-900/40 px-3 py-2.5"
                    >
                      <span className="text-sm text-gray-800 dark:text-gray-200 min-w-0 flex-1 pr-2">{label}</span>
                      <Switch
                        checked={featureFlagsDraft[key]}
                        onCheckedChange={(v) => setFeatureFlag(key, v)}
                        disabled={savingFlags}
                        id={`ff-${key}`}
                      />
                    </li>
                  ))}
                </ul>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

// Reusable info row — render as "label: value" on one line
const InfoRow: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => {
  const hasValue = value !== undefined && value !== null && !(typeof value === 'string' && value.trim() === '');
  return (
    <div className="flex items-start gap-3">
      <div className="text-sm font-medium text-gray-500 dark:text-gray-400">{label}:</div>
      <div className="text-sm text-gray-900 dark:text-white break-words">{hasValue ? value : '-'}</div>
    </div>
  );
};

